import type {
  Assignment,
  AssignmentId,
  ExpectedProgress,
  ISODateTime,
  Resource,
  Task,
  TaskProgressMode,
} from '@bigint-project/core';
import { isoDateTime, taskProgressMode } from '@bigint-project/core';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import { PROGRESS_STATUS_LABEL, progressStatusBadgeClass } from '../progress';
import { formatHours } from '../resource/format.js';

/** 對話框內顯示的一筆逐日回報（已由呼叫端換算成小時）。 */
export interface TaskProgressDailyEntry {
  readonly entryId: string;
  /** `YYYY-MM-DD`。 */
  readonly date: string;
  readonly assignmentId: AssignmentId;
  readonly resourceName: string;
  readonly hours: number;
  readonly notes: string;
}

/** 一筆待送出的逐日回報。 */
export interface TaskProgressDailyInput {
  readonly date: string;
  readonly assignmentId: AssignmentId;
  readonly hours: number;
  readonly notes: string;
  /** 回報的剩餘工時（小時）；null 代表這次不回報剩餘工時。 */
  readonly remainingHours: number | null;
}

export interface TaskProgressDialogProps {
  readonly isOpen: boolean;
  readonly task: Task;
  readonly assignments: readonly Assignment[];
  readonly resources: readonly Resource[];
  readonly statusDate: ISODateTime | null;
  /** 依 `progressAsOf` 算出的預期進度；未提供時不顯示落後徽章。 */
  readonly expectedProgress?: ExpectedProgress | null;
  /** 本任務已有的逐日回報紀錄。 */
  readonly dailyEntries?: readonly TaskProgressDailyEntry[];
  /** 上一次送出被 core 拒絕的原因；顯示在對話框內，不 alert。 */
  readonly errorMessage?: string | null;
  readonly onCancel: () => void;
  /**
   * work 模式：本次要寫入的逐日回報。
   *
   * 工時表是實際工時的唯一真相 —— 指派的實際／加班工時總量一律由 entries 回填，
   * 因此這裡刻意不提供「直接改總量」的出口。
   */
  readonly onSave: (entries: readonly TaskProgressDailyInput[]) => void;
  /** manual 模式：直接指定完成百分比。 */
  readonly onSaveManual?: (
    percentComplete: number,
    actualStart: ISODateTime | null,
    actualFinish: ISODateTime | null,
  ) => void;
}

interface DraftEntry extends TaskProgressDailyInput {
  readonly key: string;
}

const toLocalInput = (value: ISODateTime | null): string => value?.slice(0, 16) ?? '';

/** Domain datetimes are local wall-clock values; no UTC conversion is performed. */
const fromLocalInput = (value: string): ISODateTime | null =>
  value === '' ? null : isoDateTime(`${value}:00`);

const emptyDraft = (assignments: readonly Assignment[]): {
  date: string;
  assignmentId: string;
  hours: string;
  notes: string;
  remainingHours: string;
} => ({
  date: '',
  assignmentId: String(assignments[0]?.id ?? ''),
  hours: '',
  notes: '',
  remainingHours: '',
});

/**
 * 專案經理輸入「任務進度」的純 UI。
 *
 * 兩種模式（對應 core 的 `progressMode`）：
 * - `work`：只能填逐日工時；指派的實際／剩餘工時與實際起迄日都是由工時表推導出來的
 *   結果，因此在這裡一律唯讀顯示。直接改總量會在下一次逐日回報時被整批回填蓋掉。
 * - `manual`：直接指定完成百分比，工時只作成本檢視（已投入 vs 依進度應投入）。
 */
export function TaskProgressDialog({
  isOpen,
  task,
  assignments,
  resources,
  statusDate,
  expectedProgress = null,
  dailyEntries = [],
  errorMessage = null,
  onCancel,
  onSave,
  onSaveManual,
}: TaskProgressDialogProps): React.JSX.Element | null {
  const canEdit = !task.isSummary && assignments.length > 0;
  const [mode, setMode] = useState<TaskProgressMode>(() => taskProgressMode(task));
  const [percentText, setPercentText] = useState(() => String(task.percentComplete as number));
  const [actualStart, setActualStart] = useState(() => toLocalInput(task.actualStart));
  const [actualFinish, setActualFinish] = useState(() => toLocalInput(task.actualFinish));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [drafts, setDrafts] = useState<readonly DraftEntry[]>([]);
  const [draft, setDraft] = useState(() => emptyDraft(assignments));
  const titleId = useId();
  const dialogRef = useRef<HTMLFormElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const active = isOpen && canEdit;
    if (active && !wasOpenRef.current) {
      setMode(taskProgressMode(task));
      setPercentText(String(task.percentComplete as number));
      setActualStart(toLocalInput(task.actualStart));
      setActualFinish(toLocalInput(task.actualFinish));
      setErrors({});
      setDrafts([]);
      setDraft(emptyDraft(assignments));
    }
    wasOpenRef.current = active;
  }, [assignments, canEdit, isOpen, task]);

  useEffect(() => {
    if (!isOpen || !canEdit) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    firstFieldRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [canEdit, isOpen]);

  // 摘要任務或沒有資源指派時，沒有可被正式回報的 assignment progress。
  if (!isOpen || !canEdit) return null;

  const resourceNameById = new Map(resources.map((resource) => [resource.id as string, resource.name]));
  const nameFor = (assignmentId: AssignmentId): string => {
    const assignment = assignments.find((candidate) => candidate.id === assignmentId);
    return assignment === undefined
      ? String(assignmentId)
      : resourceNameById.get(assignment.resourceId as string) ?? String(assignment.resourceId);
  };

  const manualPercent = Number(percentText);
  const investedHours = assignments.reduce((sum, assignment) => sum + (assignment.actualWork as number), 0) / 60;
  const plannedHours = assignments.reduce((sum, assignment) => sum + (assignment.work as number), 0) / 60;
  const shouldHaveInvested = Number.isFinite(manualPercent)
    ? plannedHours * Math.min(100, Math.max(0, manualPercent)) / 100
    : 0;
  const investedVariance = investedHours - shouldHaveInvested;
  const completed = manualPercent >= 100;

  const submitManual = (): void => {
    const nextErrors: Partial<Record<string, string>> = {};
    if (!Number.isFinite(manualPercent) || manualPercent < 0 || manualPercent > 100) {
      nextErrors.percentComplete = '完成百分比必須介於 0 與 100 之間。';
    }
    const outputActualStart = fromLocalInput(actualStart);
    const outputActualFinish = completed ? fromLocalInput(actualFinish) : null;
    if (outputActualStart !== null && outputActualFinish !== null && outputActualFinish < outputActualStart) {
      nextErrors.actualFinish = '實際完成不得早於實際開始。';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    onSaveManual?.(manualPercent, outputActualStart, outputActualFinish);
  };

  const submitWork = (): void => {
    if (drafts.length === 0) {
      setErrors({ drafts: '請先新增至少一筆逐日工時。' });
      return;
    }
    setErrors({});
    onSave(drafts.map(({ key: _key, ...entry }) => entry));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (mode === 'manual') submitManual();
    else submitWork();
  };

  const addDraft = (): void => {
    const hours = Number(draft.hours);
    const remaining = draft.remainingHours.trim() === '' ? null : Number(draft.remainingHours);
    if (draft.date === '' || draft.assignmentId === '' || !Number.isFinite(hours) || hours < 0) {
      setErrors({ draft: '逐日工時需要日期、指派與非負的小時數。' });
      return;
    }
    if (remaining !== null && (!Number.isFinite(remaining) || remaining < 0)) {
      setErrors({ draft: '剩餘工時必須是非負數。' });
      return;
    }
    const assignmentId = draft.assignmentId as AssignmentId;
    if (drafts.some((row) => row.assignmentId === assignmentId && row.date === draft.date)) {
      setErrors({ draft: '同一指派的同一天只能回報一次。' });
      return;
    }
    setErrors({});
    setDrafts((current) => [...current, {
      key: `${draft.assignmentId}@${draft.date}`,
      date: draft.date,
      assignmentId,
      hours,
      notes: draft.notes,
      remainingHours: remaining,
    }]);
    setDraft((current) => ({ ...current, hours: '', notes: '', remainingHours: '' }));
  };

  const trapFocus = (event: ReactKeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable === undefined || focusable.length === 0) return;
    const items = [...focusable];
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <form
        aria-labelledby={titleId}
        aria-modal="true"
        className="max-h-full w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900"
        ref={dialogRef}
        onKeyDown={trapFocus}
        onSubmit={handleSubmit}
        role="dialog"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id={titleId} className="text-base font-semibold">更新任務進度</h2>
          <button type="button" onClick={onCancel} aria-label="關閉任務進度" className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">關閉</button>
        </div>
        <p className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-100">{task.name}</p>
        <p className="mb-3 rounded bg-blue-50 p-3 text-sm text-blue-900 dark:bg-blue-950 dark:text-blue-100">
          實際工時以逐日回報為準；指派的總量與實際起迄日由工時表推導，不能直接編輯。
        </p>
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
          狀態日：{statusDate === null ? '未設定' : statusDate.slice(0, 16)}
        </p>

        {expectedProgress === null ? null : (
          <p
            data-testid="task-progress-expectation"
            className={`mb-3 flex flex-wrap items-center gap-2 rounded px-3 py-2 text-sm ${progressStatusBadgeClass(expectedProgress.status)}`}
          >
            <span className="font-medium">{PROGRESS_STATUS_LABEL[expectedProgress.status]}</span>
            <span>預期進度 {Math.round(expectedProgress.expectedPercent as number)}%</span>
            <span>目前 {Math.round(expectedProgress.actualPercent as number)}%</span>
            {expectedProgress.status === 'behind' ? (
              <span data-testid="task-progress-lag">
                落後 {Math.round(((expectedProgress.lagWorkingMinutes as number) / 60) * 10) / 10} 小時工作量
              </span>
            ) : null}
          </p>
        )}

        {errorMessage === null ? null : (
          <p role="alert" data-testid="task-progress-error" className="mb-3 rounded bg-danger-soft px-3 py-2 text-sm text-danger">
            {errorMessage}
          </p>
        )}

        <fieldset className="mb-4">
          <legend className="mb-1 text-sm font-medium">進度模式</legend>
          <div className="flex gap-2">
            <label className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="progress-mode"
                value="work"
                checked={mode === 'work'}
                onChange={() => setMode('work')}
              />
              依工時反推
            </label>
            <label className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="progress-mode"
                value="manual"
                checked={mode === 'manual'}
                onChange={() => setMode('manual')}
              />
              直接指定進度
            </label>
          </div>
        </fieldset>

        {mode === 'manual' ? (
          <div data-testid="task-progress-manual" className="grid gap-3 text-sm">
            <label className="grid max-w-xs gap-1">完成百分比
              <input
                ref={firstFieldRef}
                aria-label="完成百分比"
                aria-invalid={Boolean(errors.percentComplete)}
                type="number"
                step="any"
                value={percentText}
                onChange={(event) => setPercentText(event.target.value)}
                className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600"
              />
              {errors.percentComplete ? <span className="text-xs text-red-600">{errors.percentComplete}</span> : null}
            </label>
            <dl data-testid="task-progress-cost-view" className="grid gap-2 rounded border border-slate-200 p-3 sm:grid-cols-3 dark:border-slate-700">
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">已投入工時</dt>
                <dd data-testid="task-progress-invested">{formatHours(investedHours)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">依進度應投入</dt>
                <dd data-testid="task-progress-expected-work">{formatHours(shouldHaveInvested)}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">差異</dt>
                <dd data-testid="task-progress-invested-variance" className={investedVariance > 0 ? 'text-danger' : ''}>
                  {investedVariance > 0 ? '+' : ''}{formatHours(investedVariance)}
                  {investedVariance > 0 ? '（人力投入超出預期）' : ''}
                </dd>
              </div>
            </dl>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">實際開始
                <input
                  aria-label="實際開始"
                  type="datetime-local"
                  value={actualStart}
                  onChange={(event) => setActualStart(event.target.value)}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600"
                />
              </label>
              <label className="grid gap-1">實際完成{completed ? '' : '（未達 100% 時不會儲存）'}
                <input
                  aria-label="實際完成"
                  aria-invalid={Boolean(errors.actualFinish)}
                  disabled={!completed}
                  type="datetime-local"
                  value={actualFinish}
                  onChange={(event) => setActualFinish(event.target.value)}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 disabled:opacity-50 dark:border-slate-600"
                />
                {errors.actualFinish ? <span className="text-xs text-red-600">{errors.actualFinish}</span> : null}
              </label>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table data-testid="task-progress-derived" className="w-full min-w-[560px] text-left text-sm">
                <caption className="mb-1 text-left text-xs text-slate-500 dark:text-slate-400">
                  以下數字由逐日工時推導，唯讀。
                </caption>
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300">
                    <th className="px-2 py-2 font-medium">資源</th>
                    <th className="px-2 py-2 font-medium">實際工時</th>
                    <th className="px-2 py-2 font-medium">剩餘工時</th>
                    <th className="px-2 py-2 font-medium">加班工時</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment) => (
                    <tr key={assignment.id as string} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="px-2 py-2 font-medium">{nameFor(assignment.id)}</td>
                      <td data-testid={`task-progress-actual-${assignment.id}`} className="px-2 py-2">
                        {formatHours((assignment.actualWork as number) / 60)}
                      </td>
                      <td className="px-2 py-2">{formatHours((assignment.remainingWork as number) / 60)}</td>
                      <td className="px-2 py-2">{formatHours(((assignment.overtimeWork ?? 0) as number) / 60)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p data-testid="task-progress-derived-dates" className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              完成百分比 {Math.round(task.percentComplete as number)}%
              ・實際開始 {task.actualStart?.slice(0, 16) ?? '—'}
              ・實際完成 {task.actualFinish?.slice(0, 16) ?? '—'}
            </p>

            <section data-testid="task-progress-daily" className="mt-4 rounded border border-slate-200 p-3 dark:border-slate-700">
              <h3 className="mb-2 text-sm font-medium">逐日工時</h3>
              {dailyEntries.length === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">尚無逐日回報。</p>
              ) : (
                <ul className="mb-2 grid gap-1 text-xs text-slate-600 dark:text-slate-300">
                  {dailyEntries.map((entry) => (
                    <li key={entry.entryId} data-testid={`task-progress-daily-${entry.entryId}`}>
                      {entry.date} ・ {entry.resourceName} ・ {formatHours(entry.hours)}
                      {entry.notes === '' ? '' : ` ・ ${entry.notes}`}
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid gap-2 sm:grid-cols-5">
                <input
                  ref={firstFieldRef}
                  aria-label="逐日工時日期"
                  type="date"
                  value={draft.date}
                  onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 text-sm dark:border-slate-600"
                />
                <select
                  aria-label="逐日工時指派"
                  value={draft.assignmentId}
                  onChange={(event) => setDraft((current) => ({ ...current, assignmentId: event.target.value }))}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 text-sm dark:border-slate-600"
                >
                  {assignments.map((assignment) => (
                    <option key={assignment.id as string} value={assignment.id as string}>
                      {nameFor(assignment.id)}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="逐日工時（小時）"
                  type="number"
                  min="0"
                  step="any"
                  value={draft.hours}
                  onChange={(event) => setDraft((current) => ({ ...current, hours: event.target.value }))}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 text-sm dark:border-slate-600"
                />
                <input
                  aria-label="回報剩餘工時（小時）"
                  type="number"
                  min="0"
                  step="any"
                  value={draft.remainingHours}
                  onChange={(event) => setDraft((current) => ({ ...current, remainingHours: event.target.value }))}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 text-sm dark:border-slate-600"
                />
                <input
                  aria-label="逐日工時備註"
                  type="text"
                  value={draft.notes}
                  onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
                  className="rounded border border-slate-300 bg-transparent px-2 py-1.5 text-sm dark:border-slate-600"
                />
              </div>
              {errors.draft ? <p className="mt-1 text-xs text-red-600">{errors.draft}</p> : null}
              <button
                type="button"
                onClick={addDraft}
                className="mt-2 rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
              >
                新增逐日工時
              </button>

              {drafts.length === 0 ? null : (
                <ul data-testid="task-progress-drafts" className="mt-3 grid gap-1 text-xs text-slate-700 dark:text-slate-200">
                  {drafts.map((row) => (
                    <li key={row.key} className="flex items-center gap-2">
                      <span>
                        {row.date} ・ {nameFor(row.assignmentId)} ・ {formatHours(row.hours)}
                        {row.remainingHours === null ? '' : ` ・ 剩餘 ${formatHours(row.remainingHours)}`}
                        {row.notes === '' ? '' : ` ・ ${row.notes}`}
                      </span>
                      <button
                        type="button"
                        aria-label={`移除 ${row.date} 的逐日工時`}
                        onClick={() => setDrafts((current) => current.filter((candidate) => candidate.key !== row.key))}
                        className="rounded px-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        移除
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {errors.drafts ? <p className="mt-2 text-xs text-red-600">{errors.drafts}</p> : null}
            </section>
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800">取消</button>
          <button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">儲存進度</button>
        </div>
      </form>
    </div>
  );
}
